import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, parseApiError, type AdminFlag } from '../lib/api';

import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { StatusBadge } from '../components/StatusBadge';
import { PageSpinner } from '../components/Spinner';
import { MatchRow } from '../components/MatchRow';
import { canWrite, isAdminRole } from '../lib/roles';
import { cn } from '../lib/cn';
import { SETTLE, settleDelay } from '../lib/motion';
import {
  LayoutDashboard,
  Trophy,
  Swords,
  PlayCircle,
  StopCircle,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  ArrowRight,
  Gavel,
} from 'lucide-react';

// ── Shared helpers ────────────────────────────────────────────────────────────

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

function SuccessMsg({ msg }: { msg: string }) {
  return msg ? (
    <p className="mt-2 flex items-center gap-1.5 text-xs text-green-400">
      <CheckCircle2 className="h-3.5 w-3.5" /> {msg}
    </p>
  ) : null;
}

// ── ─── SECTION 1: COMPETITIONS ────────────────────────────────────────────────

function CompRow({ comp, onRefresh }: { comp: { id: string; name: string; status: string; teamSize: number; createdAt: number }; onRefresh: () => void }) {
  const [error, setError] = useState('');

  const activate = useMutation({
    mutationFn: () => api.activateCompetition(comp.id),
    onSuccess: onRefresh,
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const end = useMutation({
    mutationFn: () => api.endCompetition(comp.id),
    onSuccess: onRefresh,
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <div className="rounded-lg bg-white/[0.04] px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-white">{comp.name || comp.id.replace('COMP#', '')}</p>
          <p className="text-xs text-ink-meta">
            {comp.teamSize}v{comp.teamSize} · Created {new Date(comp.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={comp.status} />
          {comp.status === 'PENDING' && (
            <button onClick={() => activate.mutate()} disabled={activate.isPending}
              className="flex items-center gap-1 rounded-md bg-emerald-500/10 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-500/15 disabled:opacity-50">
              <PlayCircle className="h-3.5 w-3.5" /> {activate.isPending ? '…' : 'Activate'}
            </button>
          )}
          {comp.status === 'LIVE' && (
            <button onClick={() => end.mutate()} disabled={end.isPending}
              className="flex items-center gap-1 rounded-md bg-red-500/10 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/15 disabled:opacity-50">
              <StopCircle className="h-3.5 w-3.5" /> {end.isPending ? '…' : 'End'}
            </button>
          )}
        </div>
      </div>
      <ErrorMsg msg={error} />
    </div>
  );
}

function CompetitionsSection({ onSelectComp, selectedCompId }: { onSelectComp: (id: string) => void; selectedCompId: string }) {
  const qc = useQueryClient();
  const { data: comps, isLoading } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const refresh = () => qc.invalidateQueries({ queryKey: ['competitions'] });

  if (isLoading) return <PageSpinner />;

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
          <Trophy className="h-4 w-4 text-ink-secondary" /> Competitions
        </h2>
        <Link
          to="/admin/setup"
          className="rounded-md bg-white/[0.05] px-2.5 py-1 text-xs text-ink-secondary hover:bg-white/[0.08] hover:text-ink-primary"
        >
          Create competitions in Setup → Competition
        </Link>
      </div>

      {!comps?.length ? (
        <p className="text-sm text-ink-meta">No competitions yet.</p>
      ) : (
        <div className="space-y-2">
          {comps.map(c => (
            <div key={c.id} onClick={() => onSelectComp(c.id)}
              className={cn('cursor-pointer rounded-lg transition-all', selectedCompId === c.id && 'bg-accent-500/10')}>
              <CompRow comp={c} onRefresh={refresh} />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── SECTION 3: MATCHES ────────────────────────────────────────────────────────

function MatchesSection({ compId }: { compId: string }) {
  const qc = useQueryClient();
  const { data: matches, isLoading } = useQuery({
    queryKey: ['matches', compId],
    queryFn: () => api.matches(compId),
    enabled: !!compId,
  });

  if (!compId) return (
    <Card>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <Swords className="h-4 w-4 text-ink-secondary" /> Matches
      </h2>
      <p className="text-sm text-ink-meta">Select a competition above to view matches.</p>
    </Card>
  );

  if (isLoading) return <PageSpinner />;
  const ms = matches || [];

  const onResultSaved = () => {
    qc.invalidateQueries({ queryKey: ['matches', compId] });
    qc.invalidateQueries({ queryKey: ['standings', compId] });
  };

  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <Swords className="h-4 w-4 text-ink-secondary" /> Matches
        <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs font-normal">{ms.length}</span>
      </h2>

      {ms.length === 0 ? (
        <p className="text-sm text-ink-meta">No matches yet for this competition.</p>
      ) : (
        <div className="space-y-2">
          {ms.map(m => (
            <MatchRow key={m.sk} m={m} onResultSaved={onResultSaved} />
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-ink-meta">Click any match to view details, override scores, or force resolve. Unplayed or disputed matches can also have their result entered manually below.</p>
    </Card>
  );
}

// MatchRow (clans, status, winner, expand-to-score) now lives in
// ../components/MatchRow.tsx — extracted so the Matches-step
// MatchScheduleBoard (TournamentSetup.tsx) can reuse the exact same row
// instead of duplicating it. Imported above; behavior here is unchanged.

// ── SECTION 4: STAGE CONTROLS ─────────────────────────────────────────────────

function StageControlsSection({ compId }: { compId: string }) {
  const qc = useQueryClient();
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const [seedingDeadline, setSeedingDeadline] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const comp = comps?.find(c => c.id === compId);

  const advanceMut = useMutation({
    mutationFn: () => api.advanceStage(
      compId,
      seedingDeadline ? new Date(seedingDeadline).getTime() : undefined
    ),
    onSuccess: (data) => {
      setSuccess(`Stage advanced to ${data.stage}`);
      setError('');
      qc.invalidateQueries({ queryKey: ['competitions'] });
    },
    onError: (e: Error) => { setError(parseApiError(e).message); setSuccess(''); },
  });

  if (!compId) return null;

  const currentStage = comp?.stage ?? 'None';
  const nextStage =
    !comp?.stage ? 'SEEDING'
    : comp.stage === 'SEEDING' ? 'ROUND_ROBIN'
    : comp.stage === 'ROUND_ROBIN' ? 'PLAYOFFS'
    : null;

  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <ArrowRight className="h-4 w-4 text-ink-secondary" /> Stage Controls
      </h2>

      <div className="mb-4 flex items-center gap-3">
        <span className="text-xs text-ink-meta">Current stage:</span>
        <span className="rounded-full bg-accent-500/10 px-3 py-0.5 text-xs font-semibold text-accent-500">
          {currentStage}
        </span>
        {nextStage && (
          <>
            <ArrowRight className="h-4 w-4 text-ink-meta" />
            <span className="rounded-full bg-white/[0.05] px-3 py-0.5 text-xs text-ink-secondary">
              {nextStage}
            </span>
          </>
        )}
      </div>

      {nextStage === 'SEEDING' && (
        <div className="mb-3">
          <label className="mb-1 block text-xs text-ink-secondary">
            Seeding deadline <span className="text-ink-meta">(Sunday midnight recommended)</span>
          </label>
          <input
            type="datetime-local"
            value={seedingDeadline}
            onChange={e => setSeedingDeadline(e.target.value)}
            className="input w-full [color-scheme:dark]"
          />
        </div>
      )}

      {success && <SuccessMsg msg={success} />}
      {error && <ErrorMsg msg={error} />}

      {nextStage ? (
        <button
          onClick={() => advanceMut.mutate()}
          disabled={advanceMut.isPending}
          className="btn-primary mt-2 flex items-center gap-2"
        >
          <ArrowRight className="h-4 w-4" />
          {advanceMut.isPending ? 'Advancing…' : `Advance to ${nextStage}`}
        </button>
      ) : (
        <p className="text-xs text-ink-meta">Competition is at the final stage.</p>
      )}
    </Card>
  );
}

// ── SECTION 5: FLAGS ──────────────────────────────────────────────────────────

function FlagRow({
  flag,
  onResolved,
  canResolve,
}: {
  flag: AdminFlag;
  onResolved: () => void;
  canResolve: boolean;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      api.admin.resolveFlag(flag.pk, action, note || undefined),
    onSuccess: onResolved,
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Type tone lives in the LABEL text only — no coloured card borders/fills
  // (house rule: no severity left-strips / rainbow row tints).
  const typeColors: Record<string, string> = {
    COD_ID_CHANGED: 'text-amber-400',
    COD_ID_DUPLICATE: 'text-red-400',
    SUBMISSION_ANOMALY: 'text-orange-400',
    OTHER: 'text-ink-secondary',
  };
  const labelColor = typeColors[flag.type] ?? typeColors.OTHER;

  return (
    <div className="rounded-xl bg-white/[0.04] p-4">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <span className={`text-xs font-semibold uppercase tracking-wide ${labelColor}`}>{flag.type.replace(/_/g, ' ')}</span>
          <p className="mt-1 text-sm text-white">{flag.detail}</p>
          {flag.oldValue && flag.newValue && (
            <p className="mt-1 text-xs text-ink-secondary">
              <span className="text-red-400">{flag.oldValue}</span>
              {' → '}
              <span className="text-green-400">{flag.newValue}</span>
            </p>
          )}
        </div>
        <span className="font-mono text-xs text-ink-meta">{new Date(flag.createdAt).toLocaleDateString()}</span>
      </div>

      {canResolve ? (
        <>
          <div className="mb-2">
            <input
              type="text"
              placeholder="Resolution note (optional)"
              value={note}
              onChange={e => setNote(e.target.value)}
              className="input w-full text-xs py-1.5"
            />
          </div>

          {error && <p className="mb-2 text-xs text-red-400">{error}</p>}

          <div className="flex gap-2">
            <button
              onClick={() => mut.mutate('approve')}
              disabled={mut.isPending}
              className="flex-1 rounded-lg bg-green-500/10 py-1.5 text-xs font-semibold text-green-300 hover:bg-green-500/20 disabled:opacity-40"
            >
              {mut.isPending ? '…' : '✅ Approve'}
            </button>
            <button
              onClick={() => mut.mutate('reject')}
              disabled={mut.isPending}
              className="flex-1 rounded-lg bg-red-500/10 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20 disabled:opacity-40"
            >
              {mut.isPending ? '…' : '❌ Reject'}
            </button>
          </div>
        </>
      ) : (
        <p className="text-xs text-ink-meta">
          View only. Only admins and founders can resolve flags.
        </p>
      )}
    </div>
  );
}

function FlagsSection({ canResolve }: { canResolve: boolean }) {
  const qc = useQueryClient();
  const { data: flags, isLoading } = useQuery({
    queryKey: ['admin', 'flags'],
    queryFn: () => api.admin.flags('OPEN'),
    refetchInterval: 60_000,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'flags'] });
  const openFlags = flags ?? [];

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
          <ShieldAlert className="h-4 w-4 text-accent-500" /> Flags
          {openFlags.length > 0 && (
            <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-bold text-amber-400">
              {openFlags.length}
            </span>
          )}
        </h2>
        <button onClick={refresh} className="text-xs text-ink-meta hover:text-ink-secondary">Refresh</button>
      </div>

      {isLoading ? (
        <PageSpinner />
      ) : openFlags.length === 0 ? (
        <p className="text-sm text-ink-meta">No open flags. All clear.</p>
      ) : (
        <div className="space-y-3">
          {openFlags.map(flag => (
            <FlagRow key={flag.pk} flag={flag} onResolved={refresh} canResolve={canResolve} />
          ))}
        </div>
      )}
    </Card>
  );
}

// ── SECTION 6: DISPUTES ───────────────────────────────────────────────────────

function DisputesSection({ compId }: { compId: string }) {
  const qc = useQueryClient();
  const { data: disputes, isLoading } = useQuery({
    queryKey: ['admin', 'disputes', compId],
    queryFn: () => api.admin.disputes(compId),
    enabled: !!compId,
    refetchInterval: 30_000,
  });

  if (!compId) return null;
  if (isLoading) return <PageSpinner />;

  const open = (disputes ?? []).filter(m => m.status === 'DISPUTED');
  if (open.length === 0) return null;

  return (
    <Card>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-meta">
        <Gavel className="h-4 w-4 text-red-400" /> Disputes
        <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-bold text-red-400">{open.length}</span>
      </h2>
      <div className="space-y-3">
        {open.map(m => (
          <DisputeRow key={m.sk} match={m} onResolved={() => qc.invalidateQueries({ queryKey: ['admin', 'disputes', compId] })} />
        ))}
      </div>
    </Card>
  );
}

function DisputeRow({ match, onResolved }: { match: { pk: string; sk: string; clan1: string; clan2: string; status: string; clan1DisplayName?: string; clan2DisplayName?: string }; onResolved: () => void }) {
  const [error, setError] = useState('');
  const compId = match.pk;
  const matchId = match.sk;
  // Clan display-name preservation (Lisa, 2026-07-13) — display only;
  // mut.mutate below always sends the fixed 'clan1'/'clan2' literal action,
  // never the name, so this can't affect the resolve call.
  const clan1Label = match.clan1DisplayName ?? match.clan1;
  const clan2Label = match.clan2DisplayName ?? match.clan2;

  const mut = useMutation({
    mutationFn: (action: 'clan1' | 'clan2' | 'void') =>
      api.resolveMatch(matchId, compId, action),
    onSuccess: onResolved,
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <div className="rounded-xl bg-red-500/[0.07] p-4">
      <div className="mb-3 flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />
        <span className="font-semibold text-white">{clan1Label}</span>
        <span className="text-xs text-ink-meta">vs</span>
        <span className="font-semibold text-white">{clan2Label}</span>
      </div>
      {error && <p className="mb-2 text-xs text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={() => mut.mutate('clan1')}
          disabled={mut.isPending}
          className="flex-1 rounded-lg bg-green-500/10 py-1.5 text-xs font-semibold text-green-300 hover:bg-green-500/20 disabled:opacity-40"
        >
          {clan1Label} wins
        </button>
        <button
          onClick={() => mut.mutate('clan2')}
          disabled={mut.isPending}
          className="flex-1 rounded-lg bg-green-500/10 py-1.5 text-xs font-semibold text-green-300 hover:bg-green-500/20 disabled:opacity-40"
        >
          {clan2Label} wins
        </button>
        <button
          onClick={() => mut.mutate('void')}
          disabled={mut.isPending}
          className="rounded-lg bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-ink-secondary hover:bg-white/[0.08] disabled:opacity-40"
        >
          Void
        </button>
      </div>
    </div>
  );
}

// ── Admin Panel root ──────────────────────────────────────────────────────────

export function AdminPanel() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });
  const [selectedCompId, setSelectedCompId] = useState('');

  if (!me || !isAdminRole(me.role)) {
    return (
      <Card className="text-center">
        <p className="text-gray-400">Admin access required.</p>
      </Card>
    );
  }

  const activeCompId = selectedCompId || comps?.[0]?.id || '';
  const hasWriteAccess = canWrite(me?.role);

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <LayoutDashboard className="h-7 w-7 text-ink-secondary" />
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          <p className="text-xs text-ink-meta">
            {me.role === 'founder' ? 'Founder — full access' : 'Admin — write access active until seeding closes'}
          </p>
        </div>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <HelperCard
          id="admin-panel-intro"
          title="What lives here"
          intro="Day-to-day operations for live competitions — disputes, flags, match results, and stage progression. Competitions and clans are built in Tournament Setup, not here."
          points={[
            { text: 'Disputes and Flags need attention now — resolve or acknowledge them from here.' },
            { text: 'Matches lists every scheduled match for the selected competition — enter or correct a result inline.' },
            { text: 'Stage Controls advances the selected competition through seeding → round robin → playoffs.' },
            { text: 'Competitions below is a picker + activate/end only — build and edit them in Setup.' },
          ]}
          className="mb-6"
        />
      </div>

      <div className={cn('space-y-6', SETTLE)} style={settleDelay(2)}>
        {/* Disputes — always visible to all admin roles */}
        <DisputesSection compId={activeCompId} />

        {/* Flags always shown — they need attention */}
        <FlagsSection canResolve={hasWriteAccess} />

        {hasWriteAccess && (
          <>
            <CompetitionsSection
              onSelectComp={setSelectedCompId}
              selectedCompId={activeCompId}
            />
            {activeCompId && <StageControlsSection compId={activeCompId} />}
          </>
        )}

        <MatchesSection compId={activeCompId} />
      </div>
    </div>
  );
}
